import { useCallback } from 'react';
import { useToast } from '@/context/ToastContext';
import { useNativeMessages } from '@/hooks/useNativeMessages';
import { AddToCalendarResult } from '@/types/nativeBridge.types';

// Renders nothing — listens once, app-wide, for the native calendar
// results and surfaces failures as a toast. Kept separate from
// AddToCalendarButton (which fires per booking card) so multiple mounted
// cards don't each register their own message listener and fire duplicate
// toasts for the same native response.
//
export default function NativeCalendarResultBridge() {
  const { showToast } = useToast();

  const handleAddToCalendarResult = useCallback(
    (result: AddToCalendarResult) => {
      if (!result.success) {
        showToast({
          variant: 'error',
          title: 'Could Not Add to Calendar',
          message: result.error || 'Something went wrong. Please try again.',
        });
      }
    },
    [showToast]
  );

  // Cancel/reschedule already give the member their own primary confirmation
  // (toast/confirmation screen) — the calendar delete/move is a background
  // courtesy sync, so stay silent on success and only speak up on failure.
  const handleRemoveFromCalendarResult = useCallback(
    (result: AddToCalendarResult) => {
      if (!result.success) {
        showToast({
          variant: 'error',
          title: 'Calendar Not Updated',
          message: result.error || 'Could not remove the calendar event for this booking.',
        });
      }
    },
    [showToast]
  );

  const handleUpdateCalendarEventResult = useCallback(
    (result: AddToCalendarResult) => {
      if (!result.success) {
        showToast({
          variant: 'error',
          title: 'Calendar Not Updated',
          message: result.error || 'Could not move the calendar event for this booking.',
        });
      }
    },
    [showToast]
  );

  useNativeMessages(
    undefined,
    undefined,
    handleAddToCalendarResult,
    handleRemoveFromCalendarResult,
    handleUpdateCalendarEventResult
  );

  return null;
}
